import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useNavigation } from '../../contexts/NavigationContext';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import HamburgerButton from '../Navigation/HamburgerButton';
import MobileNavigation from '../Navigation/MobileNavigation';
import DesktopNavigation from '../Navigation/DesktopNavigation';
import './Header.css';

interface HeaderProps {
  onOpenWallet?: () => void;
}

const Header: React.FC<HeaderProps> = ({ onOpenWallet }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    mobileMenuOpen,
    setMobileMenuOpen,
    isScrolled
  } = useNavigation();
  const { isMobile, isDesktop } = useBreakpoint();

  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const lastScrollYRef = useRef(0);
  const headerRef = useRef<HTMLElement>(null);

  // Check if we're on a documentation page
  const isDocsPage = location.pathname.startsWith('/docs');

  // Handle scroll visibility for mobile (keeping existing behavior)
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      if (isDesktop) {
        if (!isHeaderVisible) {
          setIsHeaderVisible(true);
        }
        return;
      }
      
      if (currentScrollY < 10) {
        setIsHeaderVisible(true);
      } else if (currentScrollY > lastScrollYRef.current + 5) {
        setIsHeaderVisible(false);
      }
      
      lastScrollYRef.current = currentScrollY;
    };

    const handleResize = () => {
      if (isDesktop && !isHeaderVisible) {
        setIsHeaderVisible(true);
      }
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [isDesktop, isHeaderVisible]);

  const navItems = [
    { name: 'Home', href: '/', isRoute: true },
    { name: 'Features', href: '#features', isRoute: false },
    { name: 'Documentation', href: '/docs', isRoute: true, hasMegaMenu: true },
    { name: 'Whitepaper', href: '/docs/whitepaper', isRoute: true },
    { name: 'Tokenomics', href: '#tokenomics', isRoute: false },
    { name: 'Scanner', href: '/scanner', isRoute: true }
  ];

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
  };

  const handleBackToHome = () => {
    navigate('/');
  };

  return (
    <>
      <motion.header
        ref={headerRef}
        className={`header ${isScrolled ? 'scrolled' : ''}`}
        initial={false}
        animate={isMobile ? { y: isHeaderVisible ? 0 : -120 } : { y: 0 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
      >
        <nav className="nav container">
          <div className="nav-brand">
            <Link to="/" className="logo">
              <img src="/USDPWhiteTransparent.png" alt="USD.P" className="logo-image" />
              <span className="logo-text"></span>
            </Link>
          </div>

          {/* Mobile Documentation Header */}
          {isMobile && isDocsPage && (
            <div className="mobile-docs-header">
              <span className="docs-title">Documentation</span>
              <button
                className="back-to-home-btn"
                onClick={handleBackToHome}
                aria-label="Back to Home"
              >
                <span className="back-arrow">←</span>
                <span className="back-text">Back to Home</span>
              </button>
            </div>
          )}

          {/* Desktop Navigation */}
          {!isMobile && (
            <DesktopNavigation
              navigationItems={navItems}
              onWalletConnect={onOpenWallet}
            />
          )}

          {/* Mobile Menu Button - Using new HamburgerButton component */}
          {isMobile && (
            <HamburgerButton
              isOpen={mobileMenuOpen}
              onClick={handleMobileMenuToggle}
              className="mobile-menu-btn"
            />
          )}
        </nav>
      </motion.header>

      {/* Mobile Navigation - Using new MobileNavigation component */}
      <MobileNavigation
        isOpen={mobileMenuOpen}
        onClose={handleMobileMenuClose}
        navigationItems={navItems}
        onWalletConnect={onOpenWallet}
      />
    </>
  );
};

export default Header;